import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { fmtINR, fmtLakh, fmtDate } from "../lib/format";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CalendarPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState({ days: [], cash_requirements: {} });
  const [dayDetail, setDayDetail] = useState(null);
  const [dayItems, setDayItems] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/dashboard/calendar?month=${month}&year=${year}`);
    setData(data);
  }, [month, year]);
  useEffect(() => { load(); }, [load]);

  const openDay = async (dstr) => {
    setDayDetail(dstr);
    const { data } = await api.get(`/dashboard/calendar/day?day=${dstr}`);
    setDayItems(data);
  };

  const first = new Date(year, month - 1, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const byDate = {};
  data.days.forEach((d) => { byDate[d.date] = d; });
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const todayStr = new Date().toISOString().slice(0, 10);

  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const cr = data.cash_requirements || {};

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head text-2xl font-extrabold">Renewal Calendar</h1>
        <div className="flex gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2"><div className="text-[11px] text-blue-600 font-bold uppercase">Next 7 Days</div><div className="font-head font-bold">{cr.next_7?.count || 0} renewals · <span className="font-mono-num">{fmtLakh(cr.next_7?.amount || 0)}</span></div></div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2"><div className="text-[11px] text-slate-600 font-bold uppercase">Next 30 Days</div><div className="font-head font-bold">{cr.next_30?.count || 0} renewals · <span className="font-mono-num">{fmtLakh(cr.next_30?.amount || 0)}</span></div></div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={prev} className="p-2 rounded-lg hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft /></button>
          <div className="font-head font-bold text-lg">{first.toLocaleString("en-US", { month: "long" })} {year}</div>
          <button onClick={next} className="p-2 rounded-lg hover:bg-slate-100" data-testid="cal-next"><ChevronRight /></button>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {DOW.map((d) => <div key={d} className="text-center text-xs font-semibold text-slate-400 uppercase">{d}</div>)}
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const dstr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const info = byDate[dstr];
            const isToday = dstr === todayStr;
            return (
              <button key={i} onClick={() => info && openDay(dstr)} disabled={!info}
                data-testid={`cal-day-${dstr}`}
                className={`aspect-square rounded-lg border p-1.5 text-left flex flex-col ${isToday ? "border-blue-500 ring-1 ring-blue-300" : "border-slate-200"} ${info ? "hover:border-blue-400 cursor-pointer bg-white" : "bg-slate-50 cursor-default"}`}>
                <span className={`text-xs font-semibold ${isToday ? "text-blue-600" : "text-slate-500"}`}>{d}</span>
                {info && (
                  <div className="mt-auto">
                    <div className="text-sm font-head font-extrabold text-red-600">{info.count}</div>
                    <div className="text-[10px] font-mono-num text-slate-500">{fmtLakh(info.amount)}</div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {dayDetail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={() => setDayDetail(null)}>
          <div className="w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="day-detail">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white">
              <div><h3 className="font-head font-bold text-lg">{fmtDate(dayDetail)}</h3><p className="text-sm text-slate-500">{dayItems.length} renewal(s) · {fmtINR(dayItems.reduce((a, c) => a + (c.amount || 0), 0))}</p></div>
              <button onClick={() => setDayDetail(null)} className="text-slate-400"><X /></button>
            </div>
            <div className="divide-y">
              {dayItems.map((c) => (
                <div key={c.id} className="p-4">
                  <div className="flex justify-between"><span className="font-semibold">{c.customer_name}</span><span className="font-mono-num font-semibold">{fmtINR(c.amount)}</span></div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.vendor_name} · {c.service_type} · {c.plan_name} · {c.city || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
